import React, { useState, useEffect } from 'react';
import { useLocation, NavLink } from 'react-router-dom';
import GascoLogo from '../../assets/images/logo.svg';

// Components
import Button from '../Button/Button';

// Styles
import './navbar.css';
import classNames from 'classnames';
import { Industria, Solucion } from '../../interfaces/types';

import {
  STRAPI_CDN_URL,
  getSolucionesEnergeticas,
  getIndustrias,
} from '../../config/services/strapi/strapi';

const NavBar = () => {
  const [soluciones, setSoluciones] = useState<Partial<Solucion>[]>([]);
  const [industrias, setIndustrias] = useState<Industria[]>([]);

  const [isOpen, setIsOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSubMenuOpen, setIsSubMenuOpen] = useState({
    soluciones: false,
    industrias: false,
  });

  const [industriaSeleccionada, setIndustriaSeleccionada] = useState<Industria | null>(null);

  const location = useLocation();

  const handleOpenMenu = () => {
    setIsOpen(true);
  }

  const handleCloseMenu = () => {
    setIsOpen(false);
  }

  useEffect(() => {
    const fetchSoluciones = async () => {
      const rawSoluciones = await getSolucionesEnergeticas();
      const mappedSoluciones: Partial<Solucion>[] = rawSoluciones.map((solucion: any) => ({
        id: solucion.id,
        beneficios: solucion.beneficios,
        caracteristicas: solucion.caracteristicas,
        descripcion: solucion.descripcion,
        externo: solucion.externo,
        icono: solucion.icono,
        imagenBeneficios: solucion.imagen_beneficios,
        imagenIndustria: solucion.imagen_industria,
        imagenProyectos: solucion.imagen_proyectos,
        industrias: solucion.industrias,
        nombre: solucion.nombre,
        portada: {
          url: `${STRAPI_CDN_URL}/${solucion.portada?.hash}${solucion.portada?.ext}`
        },
        url: solucion.url,
      }));

      setSoluciones(mappedSoluciones);
    };

    const fetchIndustrias = async () => {
      const rawIndustrias: any[] | null = await getIndustrias();
      if (!rawIndustrias) {
        setIndustrias([]);
        return;
      }

      const mappedIndustrias: Industria[] = rawIndustrias?.map((industria: any) => ({
        id: industria.id,
        nombre: industria.nombre,
        nombreIndustria: industria.nombre_industria,
        icono: {
          url: industria.icono?.url
        },
        logo: {
          url: industria.logo?.url
        },
        descripcion: industria.descripcion,
        beneficios: industria.beneficios,
        slug: industria.slug,
        banner: {
          url: industria.banner?.url
        },
        imagenBeneficios: {
          url: industria.imagen_beneficios?.url
        },
        soluciones: industria.soluciones_energeticas || []
      }));

      setIndustrias(mappedIndustrias);

      setIndustriaSeleccionada(mappedIndustrias[0] || null);
    };

    fetchIndustrias();
    fetchSoluciones();
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsSubMenuOpen({
      soluciones: false,
      industrias: false,
    });
  }, [location]);

  return (
    <header className='bg-white relative z-99 border-b border-b-[var(--border-color-primary)]'>
      <div className='bg-[var(--background-color-primary)] pt-[6.4px] md:pt-[7.4px] pb-[8px] md:pb-[9px] pl-0 pr-0'>
        <div className="flex justify-between items-center relative px-[15px] lg:px-[108px]">
          <a href="https://www.gasco.cl" className="!text-white !text-[10.56px] md:!text-[11.88px] !leading-[15.84px] md:!leading-[17.82px] lg:!text-[13.2px] !font-[400]">
            <i className="fas fa-chevron-left text-[8px] w-[5px] md:w-[5.625px] h-[8px] md:h-[9px] mr-[7.4px] md:mr-[8px]"></i>
            Volver a GASCO.cl
          </a>
        </div>
      </div>
      <div className='relative flex justify-between items-center px-[15px] lg:px-[108px] h-[70.325px] md:h-[79.2px] lg:h-[103px]'>
        <div className='w-full flex justify-start items-center gap-[2.5rem]'>
          <NavLink to='/'>
            <img className='w-[166.4px] md:min-w-[187.2px] lg:min-w-[172px] xl:min-w-[200px] 2xl:min-w-[250px] h-auto' src={GascoLogo} />
          </NavLink>
          <nav className='w-full hidden lg:flex items-center gap-[2.5rem] lg:gap-[2rem] relative'>
            <NavLink
              to='/'
              className='pr-1 text-[16px] lg:!text-[12.6px] xl:!text-[14px] 2xl:!text-[16.8px] !leading-[18.9px] text-nowrap font-[600] !text-[#292929] hover:!text-[var(--text-color-secondary)] hover:!underline !underline-offset-8 !decoration-2 transition h-auto'
            >
              Inicio
            </NavLink>
            <NavLink
              to='/soluciones'
              className='h-auto lg:h-20 lg:flex lg:items-center pr-1 text-[16px] lg:!text-[12.6px] xl:!text-[14px] 2xl:!text-[16.8px] !leading-[18.9px] text-nowrap lg:text-wrap text-center font-[600] !text-[#292929] hover:!text-[var(--text-color-secondary)] hover:!underline !underline-offset-8 !decoration-2 transition'
              onMouseEnter={() => handleOpenMenu()}
              onMouseLeave={handleCloseMenu}
            >
              <div className="flex justify-center items-center">
                <span>Solucionesㅤ</span>
                <i className="fas fa-chevron-down"></i>
              </div>
            </NavLink>
            <NavLink
              to='/casos-de-exito'
              className='pr-1 text-[16px] lg:!text-[12.6px] xl:!text-[14px] 2xl:!text-[16.8px] !leading-[18.9px] text-nowrap lg:text-wrap text-center font-[600] !text-[#292929] hover:!text-[var(--text-color-secondary)] hover:!underline !underline-offset-8 !decoration-2 transition h-auto'
            >
              Casos de Éxito
            </NavLink>
            <NavLink
              to='/podcast'
              className='pr-1 text-[16px] lg:!text-[12.6px] xl:!text-[14px] 2xl:!text-[16.8px] !leading-[18.9px] text-nowrap lg:text-wrap text-center font-[600] !text-[#292929] hover:!text-[var(--text-color-secondary)] hover:!underline !underline-offset-8 !decoration-2 transition h-auto'
            >
              Podcast
            </NavLink>
            <NavLink
              to='/articulos'
              className='pr-1 text-[16px] lg:!text-[12.6px] xl:!text-[14px] 2xl:!text-[16.8px] !leading-[18.9px] text-nowrap lg:text-wrap text-center font-[600] !text-[#292929] hover:!text-[var(--text-color-secondary)] hover:!underline !underline-offset-8 !decoration-2 transition h-auto'
            >
              Artículos
            </NavLink>
            {isOpen && <div
              className="absolute left-[0px] top-[80px] w-[calc(100%+380px)] xl:w-[calc(100%+40px)] pt-4 pb-10 !bg-white rounded-b-xl -ml-[210px] xl:-ml-10"
              onMouseEnter={() => handleOpenMenu()}
              onMouseLeave={handleCloseMenu}
            >
              <div className='px-12 pt-4'>
                <h3 className='!mx-0 !p-0 text-[18px] !font-[600] leading-[24px] !text-[#292929] !mb-4'>Soluciones por Industria</h3>
                <div className="flex justify-start items-start">
                  <ul className='border-r border-[#D7C8C8] pl-4 pr-3'>
                    {
                      industrias.map((industria: Industria) => (
                        <li key={industria.id} className="py-2">
                          <NavLink
                            className="!text-[#292929] !font-[600] !text-[16px] flex justify-start items-center group" to={`/industrias/${industria.slug}`}
                            onMouseEnter={() => setIndustriaSeleccionada(industria)}
                          >
                            <img className='w-[24px] h-[24px] mr-2 brightness-0' src={industria.icono?.url} alt={industria.nombre} />
                            <span>{industria.nombre}</span>
                            <i className={classNames('fas fa-arrow-right ml-2 opacity-0 group-hover:opacity-100 group-hover:text-[var(--text-color-secondary)] font-light transition', {
                              'text-[var(--text-color-secondary)] opacity-100': industriaSeleccionada?.id === industria.id
                            })}></i>
                          </NavLink>
                        </li>
                      ))
                    }
                  </ul>
                  <ul className='pl-10'>
                    {
                      industriaSeleccionada && industriaSeleccionada.soluciones.map((solucion: Partial<Solucion>) => (
                        <li key={solucion.id} className="py-2">
                          {
                            solucion.externo ?

                              <a
                                href={solucion.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="!text-[#292929] !font-[600] !text-[16px] hover:!underline !decoration-[var(--text-color-secondary)] !decoration-2 !underline-offset-4 transition"
                              >
                                {solucion.nombre}
                              </a>

                              :

                              <NavLink
                                to={`/soluciones/${solucion.url}`}
                                className="!text-[#292929] !font-[600] !text-[16px] hover:!underline !decoration-[var(--text-color-secondary)] !decoration-2 !underline-offset-4 transition"
                              >
                                {solucion.nombre}
                              </NavLink>
                          }
                        </li>
                      ))
                    }
                  </ul>
                </div>
              </div>
            </div>}
          </nav>
        </div>
        <div className='hidden lg:block'>
          <Button
            className='!max-w-[200px] !lg:max-w-[250px]'
            fill='outline'
            size='default'
          >
            <NavLink to='/contacto' className='text-wrap xl:text-nowrap'>
              Contacta a un especialista
            </NavLink>
          </Button>
        </div>
        <div className="block lg:hidden">
          <button
            className="w-[28px] h-[48px] text-[var(--text-color-primary)] focus:outline-none"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle mobile menu"
          >
            <i className='fas fa-bars text-2xl w-full h-[32px] text-[32px]'></i>
          </button>
        </div>
        <div className={classNames('fixed right-0 top-0 w-[300px] h-full bg-[var(--background-color-primary)] p-[32px] transition-all opacity-0 duration-500 lg:!invisible', { 'visible opacity-100 translate-x-0': isMobileMenuOpen, 'invisible translate-x-full': !isMobileMenuOpen })}>
          <button className='absolute right-[19.2px] top-[56px]' onClick={() => setIsMobileMenuOpen(false)}>
            <i className='fas fa-times text-2xl text-white w-full h-[32px] text-[32px]'></i>
          </button>
          <nav>
            <ul className='mt-[56px]'>
              <li className='mb-[15px]'>
                <NavLink
                  to='/'
                  className='block !text-[16px] text-nowrap font-[400] !leading-[24px] !text-white uppercase'
                >
                  Inicio
                </NavLink>
              </li>
              <li className='mb-[15px]'>
                <NavLink
                  to='/soluciones'
                  className='block !text-[16px] text-nowrap font-[400] !leading-[24px] !text-white uppercase'
                >
                  Soluciones
                </NavLink>
                <div className='pl-[8px] mt-[12.8px] -mb-[8px]'>
                  <h4
                    className='block !text-[16px] text-nowrap !font-[400] !leading-[19.2px] !text-white !m-0 !p-0'
                    onClick={() => setIsSubMenuOpen({ ...isSubMenuOpen, soluciones: !isSubMenuOpen.soluciones })}
                  >
                    Nuestras Soluciones
                  </h4>
                  <ul className={classNames('pt-[20px] transition-all duration-400', { 'invisible opacity-0': !isSubMenuOpen.soluciones, 'visible opacity-100': isSubMenuOpen.soluciones })}>
                    {
                      soluciones.map((solucion: Partial<Solucion>) => (
                        <li key={solucion.id} className={classNames('mb-[19.6px]', { 'hidden': !isSubMenuOpen.soluciones })}>
                          {
                            solucion.externo ?

                              <a href={solucion.url} target="_blank" rel="noopener noreferrer" className='block !text-[12.8px] text-nowrap font-[400] !leading-[19.2px] !text-white'>{solucion.nombre}</a>

                              :

                              <NavLink
                                to={`/soluciones/${solucion.url}`}
                                className='block !text-[12.8px] text-nowrap font-[400] !leading-[19.2px] !text-white'
                              >
                                {solucion.nombre}
                              </NavLink>
                          }
                        </li>
                      ))
                    }
                  </ul>
                </div>
                <div className='pl-[8px] mt-[12.8px] -mb-[8px]'>
                  <h4
                    className='block !text-[16px] text-nowrap !font-[400] !leading-[19.2px] !text-white !m-0 !p-0'
                    onClick={() => setIsSubMenuOpen({ ...isSubMenuOpen, industrias: !isSubMenuOpen.industrias })}
                  >
                    Soluciones por Industrias
                  </h4>
                  <ul className={classNames('pt-[20px] transition-all duration-400', { 'invisible opacity-0': !isSubMenuOpen.industrias, 'visible opacity-100': isSubMenuOpen.industrias })}>
                    {
                      industrias.map((industria: Industria) => (
                        <li key={industria.id} className={classNames('mb-[19.6px]', { 'hidden': !isSubMenuOpen.industrias })}>
                          <NavLink
                            to={`/industrias/${industria.slug}`}
                            className='block !text-[12.8px] text-nowrap font-[400] !leading-[19.2px] !text-white'
                          >
                            {industria.nombre}
                          </NavLink>
                        </li>
                      ))
                    }
                  </ul>
                </div>
              </li>
              <li className='mb-[15px]'>
                <NavLink
                  to='/casos-de-exito'
                  className='block !text-[16px] text-nowrap font-[400] !leading-[24px] !text-white uppercase'
                >
                  Casos de Éxito
                </NavLink>
              </li>
              <li className='mb-[15px]'>
                <NavLink
                  to='/podcast'
                  className='block !text-[16px] text-nowrap font-[400] !leading-[24px] !text-white uppercase'
                >
                  Podcast
                </NavLink>
              </li>
              <li className='mb-[15px]'>
                <NavLink
                  to='/articulos'
                  className='block !text-[16px] text-nowrap font-[400] !leading-[24px] !text-white uppercase'
                >
                  Artículos
                </NavLink>
              </li>
              <li className='mb-[15px]'>
                <NavLink
                  to='/contacto'
                  className='block !text-[16px] text-nowrap font-[400] !leading-[24px] !text-white uppercase'
                >
                  Contacta a un especialista
                </NavLink>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </header>
  )
}

export default NavBar;